import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { ModelGender, ModelProfileResponse, ModelSocialLink } from '@fetmode/types';
import { MODEL_GENDERS } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

import { useStudioModel } from '../studio/index.js';

const GENDER_LABELS: Record<ModelGender, string> = {
  WOMAN: 'common.studio.live.genderWoman',
  MAN: 'common.studio.live.genderMan',
  TRANS: 'common.studio.live.genderTrans',
  COUPLE: 'common.studio.live.genderCouple',
  NONBINARY: 'common.studio.live.genderNonbinary',
};

type SocialLinkDraft = {
  platform: string;
  url: string;
  unlockCoins: string;
};

const emptySocialLink = (): SocialLinkDraft => ({
  platform: '',
  url: '',
  unlockCoins: '0',
});

export function ModelProfileCard(): ReactElement {
  const { api } = useAuth();
  const { setProfile: setStudioProfile } = useStudioModel();
  const [profile, setProfile] = useState<ModelProfileResponse | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [scheduleNote, setScheduleNote] = useState('');
  const [isUpdatingGender, setIsUpdatingGender] = useState(false);
  const [socialLinks, setSocialLinks] = useState<ModelSocialLink[]>([]);
  const [linkDraft, setLinkDraft] = useState<SocialLinkDraft>(emptySocialLink);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    const result = await api.getModelProfile();
    if (!result.profile) {
      return;
    }
    setProfile(result.profile);
    setDisplayName(result.profile.displayName);
    setBio(result.profile.bio ?? '');
    setScheduleNote(result.profile.scheduleNote ?? '');
    setSocialLinks(result.profile.socialLinks);
  }, [api]);

  useEffect(() => {
    void load()
      .catch(() => {
        setError(t('common.studio.profile.loadError'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [load]);

  async function handleSave(): Promise<void> {
    setIsSaving(true);
    setError(null);
    setSaved(false);
    try {
      const result = await api.updateModelProfile({
        displayName: displayName.trim() || undefined,
        bio: bio.trim() || null,
        scheduleNote: scheduleNote.trim() || null,
        socialLinks,
      });
      setProfile(result.profile);
      setStudioProfile(result.profile);
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.profile.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleGenderChange(next: ModelGender): Promise<void> {
    setIsUpdatingGender(true);
    setError(null);
    try {
      const result = await api.updateModelAvailability({ gender: next });
      setProfile(result.profile);
      setStudioProfile(result.profile);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.dashboard.genderError'));
    } finally {
      setIsUpdatingGender(false);
    }
  }

  function handleAddLink(): void {
    const unlockCoins = Number.parseInt(linkDraft.unlockCoins, 10);
    if (!linkDraft.platform.trim() || !linkDraft.url.trim()) {
      setError(t('common.studio.profile.invalidLink'));
      return;
    }
    if (!Number.isFinite(unlockCoins) || unlockCoins < 0) {
      setError(t('common.studio.profile.invalidLink'));
      return;
    }
    setSocialLinks((current) => [
      ...current,
      {
        platform: linkDraft.platform.trim(),
        url: linkDraft.url.trim(),
        unlockCoins,
      },
    ]);
    setLinkDraft(emptySocialLink());
    setError(null);
  }

  function handleRemoveLink(index: number): void {
    setSocialLinks((current) => current.filter((_, i) => i !== index));
  }

  if (isLoading) {
    return <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.studio.profile.loading')}</p>;
  }

  if (!profile) {
    return <p className="fm-error">{t('common.studio.profile.loadError')}</p>;
  }

  return (
    <div className="fm-card">
      <h2 style={{ marginTop: 0, fontSize: '1rem' }}>{t('common.studio.profile.title')}</h2>
      <p style={{ margin: '0 0 1rem', color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
        {t('common.studio.profile.hint')}
      </p>

      {error ? <p className="fm-error">{error}</p> : null}
      {saved ? (
        <p style={{ margin: '0 0 0.75rem', color: 'var(--fm-color-secondary)', fontSize: '0.875rem' }}>
          {t('common.studio.profile.saved')}
        </p>
      ) : null}

      <div style={{ display: 'grid', gap: '0.75rem' }}>
        <label>
          {t('common.studio.profile.displayName')}
          <input
            className="fm-input"
            value={displayName}
            disabled={isSaving}
            onChange={(event) => {
              setDisplayName(event.target.value);
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <label>
          {t('common.studio.dashboard.genderTitle')}
          <select
            className="fm-input"
            value={profile.gender ?? ''}
            disabled={isUpdatingGender || isSaving}
            onChange={(event) => {
              const value = event.target.value as ModelGender;
              if (MODEL_GENDERS.includes(value)) {
                void handleGenderChange(value);
              }
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          >
            <option value="" disabled>
              {t('common.studio.live.genderSelect')}
            </option>
            {MODEL_GENDERS.map((value) => (
              <option key={value} value={value}>
                {t(GENDER_LABELS[value])}
              </option>
            ))}
          </select>
          <span style={{ display: 'block', marginTop: '0.25rem', color: 'var(--fm-color-text-muted)', fontSize: '0.8125rem' }}>
            {t('common.studio.dashboard.genderHint')}
          </span>
        </label>
        <label>
          {t('common.studio.profile.bio')}
          <textarea
            className="fm-input"
            value={bio}
            disabled={isSaving}
            rows={3}
            onChange={(event) => {
              setBio(event.target.value);
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem', resize: 'vertical' }}
          />
        </label>
        <label>
          {t('common.studio.profile.scheduleNote')}
          <input
            className="fm-input"
            value={scheduleNote}
            disabled={isSaving}
            onChange={(event) => {
              setScheduleNote(event.target.value);
            }}
            placeholder={t('common.studio.profile.scheduleNotePlaceholder')}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>

        <div>
          <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>
            {t('common.studio.profile.socialLinks')}
          </p>
          {socialLinks.length === 0 ? (
            <p style={{ margin: '0 0 0.5rem', color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
              {t('common.studio.profile.socialLinksEmpty')}
            </p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 0.75rem' }}>
              {socialLinks.map((link, index) => (
                <li
                  key={`${link.platform}-${link.url}-${String(index)}`}
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.5rem',
                    alignItems: 'center',
                    padding: '0.35rem 0',
                    borderBottom: '1px solid var(--fm-color-border)',
                    fontSize: '0.875rem',
                  }}
                >
                  <span style={{ flex: '1 1 8rem' }}>
                    {link.platform} · {link.url}
                    {link.unlockCoins > 0
                      ? ` · ${t('common.studio.profile.unlockCoins').replace('{{coins}}', String(link.unlockCoins))}`
                      : ''}
                  </span>
                  <Button
                    label={t('common.studio.profile.deleteLink')}
                    variant="ghost"
                    disabled={isSaving}
                    onClick={() => {
                      handleRemoveLink(index);
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            <label>
              {t('common.studio.profile.platform')}
              <input
                className="fm-input"
                value={linkDraft.platform}
                disabled={isSaving}
                onChange={(event) => {
                  setLinkDraft({ ...linkDraft, platform: event.target.value });
                }}
                style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
              />
            </label>
            <label>
              {t('common.studio.profile.url')}
              <input
                className="fm-input"
                value={linkDraft.url}
                disabled={isSaving}
                onChange={(event) => {
                  setLinkDraft({ ...linkDraft, url: event.target.value });
                }}
                style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
              />
            </label>
            <label>
              {t('common.studio.profile.unlockCoinsLabel')}
              <input
                className="fm-input"
                type="number"
                min={0}
                value={linkDraft.unlockCoins}
                disabled={isSaving}
                onChange={(event) => {
                  setLinkDraft({ ...linkDraft, unlockCoins: event.target.value });
                }}
                style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
              />
            </label>
            <Button
              label={t('common.studio.profile.addLink')}
              variant="secondary"
              disabled={isSaving}
              onClick={handleAddLink}
            />
          </div>
        </div>

        <Button
          label={isSaving ? t('common.studio.profile.saving') : t('common.studio.profile.save')}
          disabled={isSaving}
          onClick={() => {
            void handleSave();
          }}
        />
      </div>
    </div>
  );
}
